<script lang="ts">
	import { VERSION } from "$env";
	import CheckIcon from "../../assets/icons/Check.svg#icon";
	import ChevronIcon from "../../assets/icons/Chevron.svg#icon";
	import CopyIcon from "../../assets/icons/Copy.svg#icon";
	import { loggedInUser } from "../../state/loggedInUser.svelte";
	import { getTolgee } from "../../utils/i18n/getTolgee.svelte";
	import { getTranslate } from "../../utils/i18n/getTranslate.svelte";
	import { delayedVisualLoading } from "../../utils/svelte/delayedVisualLoading.svelte";
	import { createTrackedPromise } from "../../utils/svelte/trackedPromise.svelte";
	import { ButtonSize, ButtonVariant, ButtonVisualStyle } from "../buttons/Button";
	import Button from "../buttons/Button.svelte";

	let { error, open = $bindable(false) }: { error: unknown; open?: boolean } = $props();

	const tolgee = getTolgee();
	const { t } = $derived(getTranslate());

	let copied = $state<boolean>(false);
	let copyTimeout: ReturnType<typeof setTimeout>;

	const contentId = $props.id();

	const rows = $derived(
		error instanceof Error
			? [
					{ label: t("error.modal.name"), value: error.name, bold: true },
					{ label: t("error.modal.message"), value: error.message },
					{ label: t("error.modal.version"), value: VERSION },
					// eslint-disable-next-line svelte/no-top-level-browser-globals
					{ label: t("error.modal.url"), value: window.location.toString() },
					{ label: t("error.modal.user"), value: loggedInUser.value?.email ?? "—" },
					{
						label: t("error.modal.cause"),
						value: error.cause !== undefined ? JSON.stringify(error.cause) : "—",
					},
					{ label: t("error.modal.userAgent"), value: navigator.userAgent },
					{ label: t("error.modal.timestamp"), value: Temporal.Now.instant().toLocaleString(tolgee.language) },
				]
			: [],
	);

	function formatError(): string {
		if (!(error instanceof Error)) {
			return String(error);
		}
		const lines = rows.map((row) => `${row.label}: ${row.value}`);
		// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
		lines.push(`${t("error.modal.stackTrace")}:\n${error.stack || "-"}`);
		return lines.join("\n");
	}

	const copyTracked = createTrackedPromise(async (): Promise<void> => {
		await navigator.clipboard.writeText(formatError());
		copied = true;
		clearTimeout(copyTimeout);
		copyTimeout = setTimeout(() => {
			copied = false;
		}, 2000);
	});

	const copyingBusy = $derived(copyTracked.tracked.status === "pending");
	const copyingDelayedLoading = delayedVisualLoading(() => ({ loading: copyingBusy, ms: 250 }));
</script>

<div>
	<div class="flex items-center justify-between gap-4">
		<button
			type="button"
			aria-expanded={open}
			aria-controls={contentId}
			onclick={() => {
				open = !open;
			}}
			class="text-secondary-400 hover:text-secondary-600 flex flex-1 cursor-pointer items-center gap-2 text-base font-semibold not-motion-reduce:transition-colors"
		>
			<ChevronIcon
				class={[
					"size-4.5 not-motion-reduce:transition-transform not-motion-reduce:duration-200",
					open ? "-rotate-90" : "rotate-90",
				]}
			/>
			{t("error.modal.details")}
		</button>

		<Button
			size={ButtonSize.Sm}
			variant={copied ? ButtonVariant.Primary : ButtonVariant.Secondary}
			visualStyle={copied ? ButtonVisualStyle.Filled : ButtonVisualStyle.Outlined}
			startIcon={copied ? CheckIcon : CopyIcon}
			loading={copyingDelayedLoading.show}
			disabled={copyingBusy || copied}
			onclick={copyTracked.call}
		>
			{copied ? t("error.modal.copied") : t("error.modal.copy")}
		</Button>
	</div>

	<div
		id={contentId}
		class={[
			"grid not-motion-reduce:transition-[grid-template-rows] not-motion-reduce:duration-200 not-motion-reduce:ease-in-out",
			open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
		]}
	>
		<div class="min-h-0 overflow-hidden">
			<div class="border-secondary-100 bg-secondary-50 mt-3 overflow-hidden rounded-2xl border">
				{#if error instanceof Error}
					<dl class="divide-secondary-100 divide-y text-sm">
						{#each rows as row (row.label)}
							<div class="grid grid-cols-[7rem_1fr] gap-4 px-4 py-3">
								<dt class="text-secondary-400 self-start pt-px font-medium">{row.label}</dt>
								<dd class={row.bold ? "text-secondary-800 font-semibold" : "text-secondary-700"}>{row.value}</dd>
							</div>
						{/each}
						<div class="grid grid-cols-[7rem_1fr] gap-4 px-4 py-3">
							<dt class="text-secondary-400 self-start pt-px font-medium">{t("error.modal.stackTrace")}</dt>
							{#if error.stack}
								<dd class="bg-secondary-100 max-h-64 overflow-auto">
									<pre
										class="text-secondary-700 w-max rounded-xl px-3 py-2 font-mono text-xs leading-8 whitespace-break-spaces">{error.stack}</pre>
								</dd>
							{:else}
								<dd>—</dd>
							{/if}
						</div>
					</dl>
				{:else}
					<pre
						class="text-secondary-700 max-h-64 overflow-auto p-4 font-mono text-xs leading-7 whitespace-pre-wrap">{String(
							error,
						)}</pre>
				{/if}
			</div>
		</div>
	</div>
</div>
